import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { baseurl, imageUrl } from "../../../Baseurl";
import SellerNavbar from "../../../component/SellerNavbar";
import SellerSidebar from "../../../component/SellerSidebar";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import EditListingNavbar from "../SellerComp/EditListingNavbar";

const UpdateProductImage = () => {
  const [image, setImage] = useState(null); // Store selected image
  const [currentImage, setCurrentImage] = useState(null); // Store current image
  const [productId, setProductId] = useState(null); // Store the product id
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { listing_id } = useParams(); // Extract listing_id from the URL

  // Fetch current product image on component mount
  useEffect(() => {
    const fetchProductImage = async () => {
      try {
        const response = await axios.get(`${baseurl}seller/getProductImage/${listing_id}`);
        if (response.status === 200) {
          const { data } = response.data;
          if (data && data.length > 0) {
            setCurrentImage(data[0].listing_image);
            setProductId(data[0].id); // Store the product id
          }
        }
      } catch (error) {
        console.error("Error fetching product image:", error);
      }
    };

    fetchProductImage();
  }, [listing_id]);

  // File Upload Handling for the image
  const onImageDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setImage(acceptedFiles[0]); // Only store one image
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onImageDrop,
    accept: "image/*",
    multiple: false, // Only allow one file
  });

  const validateForm = () => {
    let formErrors = {};
    if (!image) formErrors.image = "Product image is required.";
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
        if (!productId) {
            console.error("ID is missing");
            Swal.fire({
                icon: "error",
                title: "Product ID Missing!",
                text: "The product ID is missing. Please reload the page.",
                confirmButtonText: "Ok",
            });
            return;
        }

        const formSubmission = new FormData();
        formSubmission.append("image", image);
        formSubmission.append("id", productId); // Send the product id, not listing_id

        // Log the form data being sent
        for (let [key, value] of formSubmission.entries()) {
            console.log(`${key}:`, value);
        }

        try {
            const response = await axios.put(`${baseurl}seller/updateProductImage`, formSubmission, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.status === 201) {
                const { message } = response.data;

                // Show success message and reload the page
                Swal.fire({
                    icon: "success",
                    title: message,
                    text: "Your product image has been updated successfully.",
                    confirmButtonText: "Ok",
                }).then(() => {
                    window.location.reload();
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Upload Failed!",
                    text: "There was an error while updating the product image. Please try again.",
                    confirmButtonText: "Ok",
                });
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            Swal.fire({
                icon: "error",
                title: "Upload Failed!",
                text: "There was an error while updating the product image. Please try again.",
                confirmButtonText: "Ok",
            });
        }

        setErrors({});
    }
};

  // Generate image preview
  const getImagePreview = image ? URL.createObjectURL(image) : null;

  return (
    <>
      <SellerNavbar />
      <div>
        <section className="mb-5">
          <div className="container-fluid">
            <div className="row my-3 g-2 justify-content-center">
              <div className="col-md-2">
                <SellerSidebar />
              </div>

              <div className="col-md-10">
                <EditListingNavbar />
                <div className="reviewcls text-start">
                  <h4 className="selh1">Update Product Image</h4>
                  <hr />

                  <div className="row">
                    <div className="col-md-6">
                      <form className="contact-form text-start border rounded p-2" onSubmit={handleSubmit}>
                        <div className="row g-2">
                          {/* Image Upload */}
                          <div className="col-md-12">
                            <label htmlFor="image" className="form-label">
                              Product Image<span className="text-danger">*</span>
                            </label>
                            <div
                              {...getRootProps({
                                className: "dropzone",
                                style: {
                                  border: "2px dashed #ccc",
                                  padding: "20px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  width: "200px",
                                  height: "200px",
                                  position: "relative",
                                },
                              })}
                            >
                              <input {...getInputProps()} />
                              <p>Drag and drop your product image here, or click to select a file</p>
                              {getImagePreview && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "0",
                                    left: "0",
                                    width: "100%",
                                    height: "100%",
                                    overflow: "hidden",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={getImagePreview}
                                    alt="Image Preview"
                                    className="p-2 rounded"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              )}

                              {/* Show current image if no new image is selected */}
                              {!getImagePreview && currentImage && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "0",
                                    left: "0",
                                    width: "100%",
                                    height: "100%",
                                    overflow: "hidden",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={`${imageUrl}listing/${currentImage}`} // Assuming the images are served from this path
                                    alt="Current Image"
                                    className="p-2 rounded"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            {errors.image && <small className="text-danger">{errors.image}</small>}
                          </div>

                          <div className="col-md-12 mt-3 text-center">
                            <button
                              type="submit"
                              className="btn submitbtnn me-2"
                              disabled={!image}
                            >
                              Update Product Image
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default UpdateProductImage;
